import Button from '@brands/Components/Button/Button';
import Container from '@brands/Components/Container/Container';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import {
  acceptAppointment,
  type AppointmentDetails,
  errorMessages,
  fetchAppointmentDetails,
  getErrorCode,
  getErrorMessage,
  getSuccessMessage,
  isSuccessCase,
} from '@brands/services/providerConfirmation';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './styles.module.scss';

const ProviderConfirmPage: React.FC = () => {
  const { confirmationToken } = useParams<{ confirmationToken: string }>();
  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);

  useEffect(() => {
    const autoAcceptAppointment = async (): Promise<void> => {
      if (!confirmationToken) {
        setError(errorMessages.INVALID_TOKEN);
        setIsLoadingDetails(false);
        return;
      }

      try {
        setIsLoadingDetails(true);

        const data = await fetchAppointmentDetails(confirmationToken);
        setAppointmentDetails(data);

        await acceptAppointment(confirmationToken);
        setSuccess(true);
        toast.success('Appointment confirmed successfully!');
      } catch (err) {
        console.error('Error auto-accepting appointment:', err);

        if (isSuccessCase(err)) {
          setSuccess(true);
          const successMessage = getSuccessMessage(err) || 'Appointment already confirmed';
          toast.success(successMessage);
        } else {
          const errorCode = getErrorCode(err);
          if (errorCode === 'ERR_PROVIDER_CONFIRMATION_NOT_FOUND' || errorCode === '[11703-0]') {
            setError(errorMessages.INVALID_TOKEN);
          } else if (errorCode === 'ERR_PROVIDER_CONFIRMATION_CANNOT_CONFIRM_DECLINED' || errorCode === '[11703-4]') {
            setError(errorMessages.CANNOT_CONFIRM_DECLINED);
          } else {
            setError(getErrorMessage(err));
          }
        }
      } finally {
        setIsLoadingDetails(false);
      }
    };

    autoAcceptAppointment();
  }, [confirmationToken]);

  const handleBackToLogin = (): void => {
    navigate('/');
  };

  const handleRequestNewLink = (): void => {
    window.open('mailto:support@dentistry.one?subject=Request New Confirmation Link', '_blank');
  };

  if (isLoadingDetails) {
    return (
      <Container>
        <div className={styles.loadingContainer}>
          <Loading dotted />
          <p className={styles.loadingText}>Confirming appointment...</p>
        </div>
      </Container>
    );
  }

  if (error && !appointmentDetails) {
    const isInvalidToken = error === errorMessages.INVALID_TOKEN;

    return (
      <Container>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2 className={styles.errorTitle}>Unable to Load Appointment</h2>
          <p className={styles.errorMessage}>{error}</p>

          {isInvalidToken ? (
            <div className={styles.errorActions}>
              <Button type="button" className={styles.requestLinkButton} onClick={handleRequestNewLink}>
                Request New Link
              </Button>
              <Button type="button" className={styles.backButton} onClick={handleBackToLogin}>
                Back to Login
              </Button>
            </div>
          ) : (
            <Button type="button" className={styles.backButton} onClick={handleBackToLogin}>
              Back to Login
            </Button>
          )}
        </div>
      </Container>
    );
  }

  if (success) {
    return (
      <Container>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>✅</div>
          <h2 className={styles.successTitle}>Appointment Confirmed</h2>
          <p className={styles.successMessage}>You will receive a reminder 10 minutes before the scheduled time.</p>
          {appointmentDetails && (
            <div className={styles.appointmentSummary}>
              <h3>Appointment Details</h3>
              <div className={styles.detailRow}>
                <span className={styles.label}>Patient:</span>
                <span className={styles.value}>{appointmentDetails.patientName}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Date & Time:</span>
                <span className={styles.value}>
                  {appointmentDetails.scheduledDate} at {appointmentDetails.scheduledTime}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Type:</span>
                <span className={styles.value}>{appointmentDetails.appointmentType}</span>
              </div>
            </div>
          )}
          <Button type="button" className={styles.backButton} onClick={handleBackToLogin}>
            Back to Login
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className={styles.confirmContainer}>
        <div className={styles.header}>
          <h1 className={styles.title}>Confirm Appointment</h1>
          <p className={styles.subtitle}>Please review the appointment details below and confirm your availability.</p>
        </div>

        {appointmentDetails && (
          <div className={styles.appointmentDetails}>
            <h2 className={styles.detailsTitle}>Appointment Information</h2>

            <div className={styles.detailCard}>
              <div className={styles.detailRow}>
                <span className={styles.label}>Patient Name:</span>
                <span className={styles.value}>{appointmentDetails.patientName}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Provider:</span>
                <span className={styles.value}>{appointmentDetails.providerName}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Appointment Type:</span>
                <span className={styles.value}>{appointmentDetails.appointmentType}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Scheduled Date:</span>
                <span className={styles.value}>{appointmentDetails.scheduledDate}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Scheduled Time:</span>
                <span className={styles.value}>{appointmentDetails.scheduledTime}</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className={styles.errorAlert}>
            <span className={styles.errorIcon}>⚠️</span>
            <span className={styles.errorText}>{error}</span>
          </div>
        )}

        <div className={styles.footer}>
          <p className={styles.footerText}>If you have any questions, please contact our support team.</p>
        </div>
      </div>
    </Container>
  );
};

export default ProviderConfirmPage;
